/**
 * StatusUpdateModal — small Founder-only status editor.
 *
 * Wraps the shadcn Dialog. Not a full PM editor: milestone, new status,
 * note, current blocker, next action.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Save } from "lucide-react";
import { STATUSES } from "./progressCalc";
import { STATUS_META } from "./StatusPill";
import { postStatusUpdate } from "@/lib/gtm";

export default function StatusUpdateModal({ open, onOpenChange, milestone, onSaved }) {
    const [status, setStatus] = useState(milestone?.status ?? "IN_PROGRESS");
    const [note, setNote] = useState("");
    const [currentBlocker, setCurrentBlocker] = useState(milestone?.current_blocker ?? "");
    const [nextAction, setNextAction] = useState(milestone?.next_action ?? "");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setStatus(milestone?.status ?? "IN_PROGRESS");
        setNote("");
        setCurrentBlocker(milestone?.current_blocker ?? "");
        setNextAction(milestone?.next_action ?? "");
        setError(null);
    }, [milestone]);

    if (!milestone) return null;

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            const res = await postStatusUpdate({
                milestone: milestone.code,
                status,
                note: note || null,
                current_blocker: currentBlocker || null,
                next_action: nextAction || null,
                source: "MANUAL_FOUNDER_UPDATE",
            });
            onSaved?.(res.milestone);
            onOpenChange(false);
        } catch (err) {
            setError(err?.detail?.detail || err?.message || "Update failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="gtm-status-update-modal"
                className="bg-qoryn-bg border-qoryn-border text-qoryn-text max-w-md"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-[18px] tracking-tight">
                        Update Milestone Status
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">Milestone</div>
                        <div className="mt-1 text-[14px] text-qoryn-text">{milestone.title}</div>
                        <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted mt-0.5">{milestone.code}</div>
                    </div>
                    <div>
                        <label className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">Status</label>
                        <select
                            data-testid="gtm-status-select"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="mt-1.5 w-full h-10 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none text-[14px] text-qoryn-text qoryn-focus"
                        >
                            {STATUSES.map((s) => (
                                <option key={s} value={s}>{STATUS_META[s]?.label ?? s}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">Note</label>
                        <textarea
                            data-testid="gtm-status-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            maxLength={2000}
                            className="mt-1.5 w-full px-3 py-2 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none text-[13px] text-qoryn-text resize-none qoryn-focus"
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <label className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">Current blocker</label>
                            <input
                                data-testid="gtm-status-blocker"
                                value={currentBlocker}
                                onChange={(e) => setCurrentBlocker(e.target.value)}
                                maxLength={500}
                                className="mt-1.5 w-full h-9 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none text-[13px] text-qoryn-text qoryn-focus"
                            />
                        </div>
                        <div>
                            <label className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">Next action</label>
                            <input
                                data-testid="gtm-status-next"
                                value={nextAction}
                                onChange={(e) => setNextAction(e.target.value)}
                                maxLength={500}
                                className="mt-1.5 w-full h-9 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none text-[13px] text-qoryn-text qoryn-focus"
                            />
                        </div>
                    </div>
                    {error && (
                        <div data-testid="gtm-status-error" className="rounded-md border border-red-500/40 bg-red-500/[0.06] px-3 py-2 font-mono-ui text-[10px] tracking-wider text-red-300">
                            {String(error)}
                        </div>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                        <button
                            type="submit"
                            data-testid="gtm-status-submit"
                            disabled={busy}
                            className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-qoryn-cyan text-black font-mono-ui text-[10px] tracking-widest uppercase hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all qoryn-focus"
                        >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" strokeWidth={1.75} />}
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="h-9 px-3 rounded-md border border-qoryn-border text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] font-mono-ui text-[10px] tracking-widest uppercase transition-colors qoryn-focus"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
